<template>
  <article>
    <activity-stage :customRootContainerClass="'dolch-dynamite'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" style="height: 100%; position: relative;">
        <div :class="{ 'barrel-stage': true, isMobile }">
          <div class="typing-stage" v-if="currentRound && currentSection === 'barrel'">
            <div
              :class="`td-container step-${index}`"
              v-for="(step, index) in currentRound.steps"
              :key="step.id || index"
            >
              <blinking-cursor-text :text="step.answer" :text-step="index"></blinking-cursor-text>
            </div>
          </div>
          <div class="spark-container" ref="sparkContainer">
            <img alt="Spark" :src="imagePaths.sparkPath" />
          </div>
          <div class="fuse-container" ref="fuseContainer">
            <img alt="Fuse" :src="imagePaths.fusePath" />
          </div>
          <div class="barrel-container">
            <div class="full-barrel">
              <img alt="Barrel" :src="imagePaths.barrelPath" />
            </div>
            <div class="blow-up-barrel">
              <img alt="Barrel lid" class="barrel-lid" :src="imagePaths.barrelLidPath" />
              <img alt="Blown up barrel" :src="imagePaths.barrelBlownUpPath" />
            </div>
          </div>
        </div>
        <div class="fireworks-stage" v-if="currentRound && currentSection === 'fireworks'">
          <div
            class="td-container"
            v-for="(step, stepIndex) in currentRound.steps"
            :key="step.id || stepIndex"
          >
            <div class="gradient-overlay"></div>
            <img alt="fireworks" :src="fireworksImage" />
            <blinking-cursor-text
              :class="`type-line fireworks-step-${stepIndex}`"
              :text="step.answer"
              :text-step="stepIndex"
            ></blinking-cursor-text>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import shuffle from 'lodash/shuffle';
import flatten from 'lodash/flatten';

import AssetLoader from 'mixins/AssetLoader';
import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import SuccessCheckSolidIcon from 'components/icons/SuccessCheckSolidIcon.vue';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import firework0Path from 'images/activities/dolch-dynamite/firework-0.png';
import firework1Path from 'images/activities/dolch-dynamite/firework-1.png';
import firework2Path from 'images/activities/dolch-dynamite/firework-2.png';
import firework3Path from 'images/activities/dolch-dynamite/firework-final.png';
import fusePath from 'images/activities/dolch-dynamite/fuse.png';
import sparkPath from 'images/activities/dolch-dynamite/spark.gif';
import barrelPath from 'images/activities/dolch-dynamite/barrel.png';
import barrelBlownUpPath from 'images/activities/dolch-dynamite/barrel-blown-up.png';
import barrelLidPath from 'images/activities/dolch-dynamite/barrel-lid.png';

import barrelExplosion from 'audio/sfx/barrel-explosion.mp3';
import firecracker from 'audio/sfx/firecracker.mp3';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    SuccessCheckSolidIcon
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, AccessibilityHelper],

  data() {
    return {
      fuseContainer: null,
      sparkContainer: null,
      activityName: 'DolchDynamite',
      currentSection: 'barrel',
      fireworksImagePaths: shuffle([firework0Path, firework1Path, firework2Path, firework3Path]),
      imagePaths: {
        fusePath,
        sparkPath,
        barrelPath,
        barrelLidPath,
        barrelBlownUpPath
      },
      sfx: {
        barrelExplosion: null,
        firecracker: null,
        soundsPerRound: null
      }
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    fireworksImage() {
      return this.fireworksImagePaths[this.progress.roundIndex];
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds();
      this.sfx = await this.loadSounds();
    },

    async loadSounds() {
      const soundPathsPerRound = this.rounds.map(round =>
        round.steps.map(step => step.answerAudio?.audio?.src).filter(Boolean)
      );

      this.queueAudio(firecracker, barrelExplosion, ...flatten(soundPathsPerRound));

      const cache = await this.downloadAudio();

      return {
        barrelExplosion: () => cache.get(barrelExplosion).play(),
        firecracker: () => cache.get(firecracker).play(),
        soundsPerRound: soundPathsPerRound.map(round =>
          round.map(soundPath => cache.get(soundPath))
        )
      };
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;

      return {
        keyboardType,
        roofs
      };
    },

    startRound() {
      this.currentSection = 'barrel';
      this.setupNewRound();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        const startRoundTL = new TimelineLite();
        startRoundTL
          .to('.barrel-stage', 0.5, { autoAlpha: 1 })
          .staggerTo('.td-container', 0.25, { autoAlpha: 1 }, 0.1)
          .call(this.enableKeyboard);
      });
    },

    handleCorrectKey(attempt) {
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentWord) {
        if (this.currentSection === 'barrel') {
          this.sndBell();
          this.finishBarrelSectionStep();
        } else {
          this.sfx.firecracker();
          TweenMax.to(`.fireworks-step-${this.progress.currentStep}`, 0.25, {
            autoAlpha: 0
          });
          this.finishFireworksSectionStep();
        }
      } else {
        this.sndType();
      }
    },

    async finishBarrelSectionStep() {
      const { roundIndex, stepIndex } = this.progress;
      let burnLimitAbsoluteX = 0;

      this.disableKeyboard();
      this.sfx.soundsPerRound[roundIndex][stepIndex].play();

      if (this.isLastStepInRound) {
        await this.burnRestOfFuse();
        this.sfx.barrelExplosion();
        await this.blowUpBarrel();

        TweenMax.to('.barrel-stage', 0.5, {
          autoAlpha: 0,
          onComplete: () => {
            this.resetBarrelStage();
            this.startFireworksPhase();
          }
        });
      } else {
        burnLimitAbsoluteX = document
          .getElementsByClassName(`td-container step-${stepIndex + 1}`)[0]
          .getBoundingClientRect().left;

        await this.burnFuseSegment(this.getFuseOffset(burnLimitAbsoluteX));
        this.nextStep({ stepCallback: this.stepCallback });
      }
    },

    startFireworksPhase() {
      this.currentSection = 'fireworks';
      this.nextStep({ isFirstStep: true, enableKeyboard: false, stepCallback: this.stepCallback });

      this.$nextTick(() => {
        const fireworksBeginningTL = new TimelineLite();

        fireworksBeginningTL
          .to('.fireworks-stage', 0.5, { autoAlpha: 1 })
          .staggerFromTo('.td-container', 0.25, { autoAlpha: 0 }, { autoAlpha: 1 }, 0.1)
          .call(this.enableKeyboard);
      });
    },

    finishFireworksSectionStep() {
      this.disableKeyboard();

      if (this.isLastStepInRound) {
        TweenMax.staggerTo(
          '.td-container',
          0.25,
          {
            autoAlpha: 0
          },
          0.1,
          () => {
            TweenMax.set('.fireworks-stage', { autoAlpha: 0 });
          }
        );
        if (this.isLastRound) {
          TweenMax.delayedCall(1.5, () => {
            TweenMax.staggerTo('#successIconContainer', 0.25, {
              autoAlpha: 0
            });
            this.completeActivity();
          });
        } else {
          TweenMax.delayedCall(1.5, this.startRound);
        }
      } else {
        this.nextStep({ stepCallback: this.stepCallback });
      }
    },

    resetBarrelStage() {
      const { height, width } = document
        .getElementsByClassName('fuse-container')[0]
        .getElementsByTagName('img')[0]
        .getBoundingClientRect();

      TweenMax.set('.fuse-container', { height, width });
      TweenMax.set('.spark-container', {
        rotation: 0,
        x: 0,
        y: 0,
        transformOrigin: '0 0',
        autoAlpha: 1
      });
      TweenMax.set('.barrel-lid', { rotation: 0, x: 0, y: 0 });
      TweenMax.set('.full-barrel', { autoAlpha: 1 });
    },

    getFuseOffset(absoluteX) {
      const { fuseContainer } = this.$refs;
      const { left } = fuseContainer.getBoundingClientRect();

      return absoluteX - left;
    },

    getTransformCoordinates(element, alterX = 0, alterY = 0) {
      const transformCoordinates = window.getComputedStyle(element).transform.split(',');

      return transformCoordinates[0] === 'none'
        ? {
            x: alterX,
            y: alterY
          }
        : {
            x: parseInt(transformCoordinates[4], 10) + alterX,
            y: parseInt(transformCoordinates[5], 10) + alterY
          };
    },

    burnFuseSegment(amount) {
      const self = this;
      const { fuseContainer } = this.$refs;
      const { sparkContainer } = this.$refs;

      return new Promise(resolve => {
        TweenMax.to(this.$refs.fuseContainer, 0.5, {
          width: fuseContainer.offsetWidth - amount
        });
        TweenMax.to(this.$refs.sparkContainer, 0.5, {
          ...self.getTransformCoordinates(sparkContainer, amount),
          onComplete: resolve
        });
      });
    },

    burnRestOfFuse() {
      const self = this;
      const { fuseContainer } = this.$refs;
      const fuseImg = fuseContainer.getElementsByTagName('img')[0];
      const { width, right } = fuseImg.getBoundingClientRect();
      const absX = right - Math.floor(width / 5) + 25;

      return new Promise(resolve => {
        self.burnFuseSegment(self.getFuseOffset(absX)).then(() => {
          self.burnRestOfCurvedFuse(0.05).then(resolve);
        });
      });
    },

    burnRestOfCurvedFuse(duration = 0.5) {
      const self = this;
      const { fuseContainer } = this.$refs;
      const { sparkContainer } = this.$refs;
      const formerAbsLeft = fuseContainer.getBoundingClientRect().left;
      const formerAbsTop = fuseContainer.getBoundingClientRect().top;

      return new Promise(resolve => {
        TweenMax.to(fuseContainer, duration, {
          height: fuseContainer.offsetHeight - 100
        });
        TweenMax.to(sparkContainer, duration, {
          rotation: -20,
          transformOrigin: '-150px -90px',
          onComplete() {
            TweenMax.to(fuseContainer, duration, {
              height: fuseContainer.offsetHeight - 100
            });
            TweenMax.fromTo(
              sparkContainer,
              duration,
              {
                ...self.getTransformCoordinates(
                  sparkContainer,
                  fuseContainer.getBoundingClientRect().left - formerAbsLeft,
                  fuseContainer.getBoundingClientRect().top - formerAbsTop
                ),
                rotation: -20,
                transformOrigin: '-150px -90px'
              },
              {
                y: -100,
                onComplete() {
                  TweenMax.to(fuseContainer, duration, {
                    height: fuseContainer.offsetHeight - 10
                  });
                  TweenMax.to(sparkContainer, duration, {
                    ...self.getTransformCoordinates(sparkContainer, 15, -30),
                    onComplete() {
                      TweenMax.set(fuseContainer, {
                        width: 80
                      });
                      TweenMax.to(fuseContainer, duration, {
                        width: fuseContainer.offsetWidth - 20
                      });
                      TweenMax.to(sparkContainer, duration, {
                        ...self.getTransformCoordinates(sparkContainer, 25, -25),
                        onComplete() {
                          TweenMax.to(fuseContainer, duration, {
                            width: fuseContainer.offsetWidth - 20
                          });
                          TweenMax.to(sparkContainer, duration, {
                            ...self.getTransformCoordinates(sparkContainer, 20),
                            onComplete() {
                              TweenMax.to(fuseContainer, duration, {
                                width: fuseContainer.offsetWidth - 35
                              });
                              TweenMax.to(sparkContainer, duration, {
                                ...self.getTransformCoordinates(sparkContainer, 20, 35),
                                onComplete: resolve
                              });
                            }
                          });
                        }
                      });
                    }
                  });
                }
              }
            );
          }
        });
      });
    },

    blowUpBarrel() {
      TweenMax.set('.spark-container', { autoAlpha: 0 });
      TweenMax.set('.full-barrel', { autoAlpha: 0 });

      return new Promise(resolve => {
        TweenMax.to('.barrel-lid', 0.25, {
          rotation: 360,
          repeat: 10
        });
        TweenMax.to('.barrel-lid', 0.5, {
          x: 300,
          y: -300,
          onComplete: resolve
        });
      });
    }
  },

  watch: {
    expectedKey() {
      if (!this.currentStep) return;

      const isMultipleWords = this.currentStep.answer.split(' ').length > 1;
      const instructions = `Type the word${isMultipleWords ? 's' : ''} - ${
        this.currentStep.answer
      }`;

      if (instructions === this.currentScreenReaderAlert) {
        return;
      }

      this.setScreenReaderAlert(instructions, 'screenReaderAlert');
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-instructions.dolch-dynamite {
  visibility: hidden;
}

.dolch-dynamite {
  width: 100%;
  height: 100%;
  position: relative;

  .barrel-stage {
    position: absolute;
    width: 1024px;
    height: auto;
    top: 0;
    left: 0;
    visibility: hidden;

    .typing-stage {
      position: absolute;
      left: 70px;
      top: 240px;

      .td-container {
        display: inline-block;
        font-size: 40px;
        margin-right: 60px;
        visibility: hidden;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    .spark-container,
    .barrel-container,
    .fuse-container {
      position: absolute;
    }

    .barrel-container {
      z-index: 0;
      top: 145px;
      right: 0px;
      position: absolute;
      width: 210px;

      .full-barrel {
        position: relative;
        z-index: 1;
      }

      .blow-up-barrel {
        position: absolute;
        top: 0;
        z-index: 0;

        .barrel-lid {
          position: absolute;
          top: 0;
          left: 10px;
        }
      }
    }

    .fuse-container {
      img {
        float: right;
      }

      overflow: hidden;
      z-index: 1;
      top: 130px;
      right: 155px;
    }

    .spark-container {
      z-index: 2;
      top: 320px;
      left: 35px;
    }

    &.isMobile {
      .barrel-container {
        top: 15px;
      }

      .fuse-container {
        top: 0px;
      }

      .spark-container {
        top: 200px;
      }

      .typing-stage {
        top: 125px;
      }
    }
  }

  .fireworks-stage {
    display: flex;
    justify-content: space-evenly;
    left: 0px;
    position: absolute;
    text-align: center;
    top: 0px;
    visibility: hidden;
    width: 100%;

    .td-container {
      display: inline-block;
      margin-right: 50px;
      position: relative;
      font-size: 25px;
      position: relative;

      .gradient-overlay {
        position: absolute;
        left: 17.5%;
        top: 17.5%;
        width: 60%;
        height: 60%;
        background: radial-gradient(white 35%, rgba(255, 255, 255, 0) 65%);
      }

      &:last-child {
        margin-right: 0;
      }

      img {
        height: 150px;
        width: 150px;
      }

      .type-line {
        position: absolute;
        top: 46%;
        left: 46%;
        transform: translate(-50%, -50%);
      }
    }
  }
}

.chrome-os {
  .dolch-dynamite {
    .barrel-container {
      top: 15px;
    }

    .fuse-container {
      top: 0px;
    }

    .spark-container {
      top: 200px;
    }

    .typing-stage {
      top: 125px;
    }
  }
}

.tablet {
  .dolch-dynamite {
    .fireworks-stage {
      width: calc(100% - 77px);
    }
  }
}
</style>
